import { FC, MouseEvent, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CONTENT_ANALYSIS_ROUTE } from 'src/constants/routes'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import { useCustomLink } from 'src/hooks/useCustomLink'
import useOutsideClick from 'src/hooks/useOutsideClick'
import { CampaignsChart } from 'src/pages/Campaigns/CampaignsChart'
import { ContentAnalysisType } from 'src/services/contentAnalysisApi/interfaces/contentAnalysis.interface'
import { getDate } from 'src/utils/date'
import { numberWithCommas } from 'src/utils/numberFormat'
import MenuThreeDotsIcon from 'src/assets/svg/menuThreeDots.svg'
import { ReactComponent as CommentIcon } from 'src/assets/svg/comments.svg'
import { ReactComponent as Loudspeaker } from 'src/assets/svg/loudspeaker.svg'
import { ReactComponent as ReactionIcon } from 'src/assets/svg/reactions.svg'
import { ReactComponent as ShareIcon } from 'src/assets/svg/shares.svg'
import { ReactComponent as ShieldIcon } from 'src/pages/Campaigns/CampaignCard/svg/brand-managment.svg'
import { ReactComponent as CompetitorIcon } from 'src/pages/Campaigns/CampaignCard/svg/competitor-analysis.svg'
import { ReactComponent as MarketIcon } from 'src/pages/Campaigns/CampaignCard/svg/market-reasearch.svg'
import { ReactComponent as ReviewIcon } from 'src/pages/Campaigns/CampaignCard/svg/review-analysis.svg'
import { Tooltip } from 'src/components/Tooltip'
import styles from './contentAnalysisCard.module.scss'

const KEYWORD_COLORS = ['#8080FF', '#F59E0B', '#10B981']

// Shared with the detail page's header pill.
export const CATEGORY_CONFIG: Record<
  string,
  { bg: string; color: string; icon: FC<React.SVGProps<SVGSVGElement>> }
> = {
  'Brand Management': { bg: '#8080FF33', color: '#8080FF', icon: ShieldIcon },
  'Competitor Analysis': {
    bg: '#8080FF33',
    color: '#8080FF',
    icon: CompetitorIcon,
  },
  'Market Research': { bg: '#8080FF33', color: '#8080FF', icon: MarketIcon },
  'Review Analysis': { bg: '#8080FF33', color: '#8080FF', icon: ReviewIcon },
}

interface ContentAnalysisCardProps {
  data: ContentAnalysisType
  onActivate?: () => void
  onComplete?: () => void
  onDelete: () => void
  onDuplicate?: () => void
  onEdit: () => void
  onRename?: () => void
}

const ContentAnalysisCard: FC<ContentAnalysisCardProps> = ({
  data,
  onActivate,
  onComplete,
  onDelete,
  onDuplicate,
  onEdit,
  onRename,
}) => {
  const dropDownRef = useRef<HTMLDivElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const navigate = useNavigate()
  const detailLink = useCustomLink(`${CONTENT_ANALYSIS_ROUTE}/${data._id}`)

  useOutsideClick({
    onOutsideClick: () => {
      setIsDropdownOpen(false)
    },
    ref: dropDownRef,
  })

  const handleCardClick = (event: MouseEvent<HTMLDivElement>) => {
    if (
      (event.target as HTMLDivElement).id !== 'dropdownMenuButton' &&
      (event.target as HTMLDivElement).id !== 'deleteButtonId'
    ) {
      navigate(detailLink)
    }
  }

  const topPhrases = data.topPhrases || []
  const quickStats = data.quickStats || { comments: 0, reactions: 0, shares: 0 }
  const sources = (data.sources || [])
    .map((s) => (typeof s === 'string' ? { link: null, platform: s } : s))
    .filter((s) => typeof s.platform === 'string' && s.platform)

  const totalPosts = data.totalPosts || 0
  const maxPlatforms = totalPosts >= 100 ? 2 : 3

  const cat = data.categories?.[0]
  const catConfig = cat ? CATEGORY_CONFIG[cat] : null
  const CatIcon = catConfig?.icon ?? null

  return (
    <div className={styles.card} onClick={handleCardClick}>
      <div className={styles.cardHeader}>
        <div className={styles.titleSection}>
          <span className={styles.title}>{data.title}</span>
          <div className={styles.subtitle}>
            <span className={styles.postCount}>{totalPosts} Posts</span>
            {sources.length > 0 && (
              <>
                <span className={styles.divider}>|</span>
                <div className={styles.platforms}>
                  {sources.slice(0, maxPlatforms).map((source, index) => (
                    <span className={styles.platformItem} key={source.platform}>
                      <span className={styles.platformIcon}>
                        {SOCIAL_ICONS[source.platform.toLowerCase()]}
                      </span>
                      <span className={styles.platformName}>
                        {platformDisplayLabel(
                          source.platform,
                          source.platform.charAt(0).toUpperCase() +
                            source.platform.slice(1)
                        )}
                      </span>
                      {index < Math.min(sources.length, maxPlatforms) - 1 &&
                        ','}
                    </span>
                  ))}
                  {sources.length > maxPlatforms && (
                    <Tooltip
                      overlay={
                        <div className={styles.platformTooltip}>
                          {sources.slice(maxPlatforms).map((s) => (
                            <div
                              className={styles.platformTooltipItem}
                              key={s.platform}
                            >
                              <span className={styles.platformTooltipIcon}>
                                {SOCIAL_ICONS[s.platform.toLowerCase()]}
                              </span>
                              <span>
                                {platformDisplayLabel(
                                  s.platform,
                                  s.platform.charAt(0).toUpperCase() +
                                    s.platform.slice(1)
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      }
                      overlayClassName="contentCardTooltip"
                      overlayInnerClassName={styles.platformTooltipInner}
                      placement="top"
                    >
                      <span
                        className={styles.morePlatforms}
                        onClick={(e) => e.stopPropagation()}
                      >
                        +{sources.length - maxPlatforms}
                      </span>
                    </Tooltip>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className={styles.headerRight}>
          {catConfig && CatIcon && (
            <div
              className={styles.categoryPill}
              style={{ background: catConfig.bg, color: catConfig.color }}
            >
              <CatIcon className={styles.categoryPillIcon} />
              <span>{cat}</span>
            </div>
          )}

          <div className={styles.dropdown} ref={dropDownRef}>
            <button
              className={styles.dropdownButton}
              onClick={(e) => {
                e.stopPropagation()
                setIsDropdownOpen((prev) => !prev)
              }}
            >
              <img alt="menu icon" src={MenuThreeDotsIcon} />
            </button>

            {isDropdownOpen && (
              <div
                className={styles.dropdownMenu}
                onClick={(e) => e.stopPropagation()}
              >
                {onRename && (
                  <div className={styles.dropdownItem} onClick={onRename}>
                    Rename
                  </div>
                )}
                <div className={styles.dropdownItem} onClick={onEdit}>
                  Edit
                </div>
                {data.status?.toLowerCase() !== 'completed' && onComplete && (
                  <div className={styles.dropdownItem} onClick={onComplete}>
                    Complete
                  </div>
                )}
                {data.status?.toLowerCase() === 'completed' && onActivate && (
                  <div className={styles.dropdownItem} onClick={onActivate}>
                    Activate
                  </div>
                )}
                {onDuplicate && (
                  <div className={styles.dropdownItem} onClick={onDuplicate}>
                    Duplicate
                  </div>
                )}
                <div className={styles.dropdownItem} onClick={onDelete}>
                  Delete
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.statsContainer}>
          <div className={styles.quickStats}>
            <div className={styles.statsTitle}>Quick Stats</div>
            <div className={styles.statRow}>
              <ReactionIcon className={styles.statIcon} />
              <span className={styles.statLabel}>Reactions</span>
              <span className={styles.statValue}>
                {numberWithCommas(quickStats.reactions)}
              </span>
            </div>
            <div className={styles.statRow}>
              <CommentIcon className={styles.statIcon} />
              <span className={styles.statLabel}>Comments</span>
              <span className={styles.statValue}>
                {numberWithCommas(quickStats.comments)}
              </span>
            </div>
            <div className={styles.statRow}>
              <ShareIcon className={styles.statIcon} />
              <span className={styles.statLabel}>Shares</span>
              <span className={styles.statValue}>
                {numberWithCommas(quickStats.shares)}
              </span>
            </div>
          </div>

          <div className={styles.topPhrases}>
            <div className={styles.statsTitle}>Top Phrases</div>
            {topPhrases.slice(0, 3).map((kw, index) => (
              <div className={styles.phraseRow} key={kw.keyword}>
                <span
                  className={styles.phraseDot}
                  style={{ backgroundColor: KEYWORD_COLORS[index] }}
                />
                <span className={styles.phraseText} title={kw.keyword}>
                  {kw.keyword}
                </span>
                <span className={styles.phraseSeparator}>•</span>
                <span className={styles.phraseCount}>
                  {numberWithCommas(kw.count)}
                </span>
                <Loudspeaker className={styles.phraseIcon} />
              </div>
            ))}
          </div>
        </div>

        <div className={styles.chartWrapper}>
          <CampaignsChart
            negativeMentionsCount={data.negatives || 0}
            neutralMentionsCount={data.neutrals || 0}
            positiveMentionsCount={data.positives || 0}
          />
        </div>
      </div>

      <div className={styles.dates}>
        {data.updatedAt && `Last Updated at ${getDate(data.updatedAt)}`}
        <span>|</span>
        {data.createdAt && `Created at ${getDate(data.createdAt)}`}
      </div>
    </div>
  )
}

export default ContentAnalysisCard
